"use client"

import { cn } from "@/lib/utils"
import { SOURCE_FILTER_ROW, sourceFilterPill } from "@/components/source-filter-pill"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ChevronDown } from "@/lib/icons"
import { useTranslations } from "next-intl"

export interface AudioChanFilters {
  /** all = show everything, sfw = SFW only, nsfw = 18+ only */
  contentMode: "all" | "sfw" | "nsfw"
  audience: "all" | "F4M" | "F4F" | "F4A" | "M4F" | "M4M" | "M4A"
  sort: "trending" | "new" | "top"
}

export const DEFAULT_AUDIOCHAN_FILTERS: AudioChanFilters = {
  contentMode: "all",
  audience: "all",
  sort: "trending",
}

interface AudioChanFilterStripProps {
  /** Kept for existing callers. The strip always renders inside the shared SourceFilterBar, which supplies the surface. */
  inline?: boolean
  filters: AudioChanFilters
  onChange: (filters: AudioChanFilters) => void
}

const CONTENT_OPTIONS = [
  { value: "all", label: "All" },
  { value: "sfw", label: "SFW" },
  { value: "nsfw", label: "18+" },
] as const

const AUDIENCE_OPTIONS = ["F4M", "F4F", "F4A", "M4F", "M4M", "M4A"] as const

const SORT_OPTIONS = [
  { value: "trending", label: "Trending" },
  { value: "new", label: "New" },
  { value: "top", label: "Top" },
] as const

const ANY = "all"

/**
 * AudioChan's filter strip, built from the same pieces as every other source strip (shared pill, shared menu), so it follows the
 * theme: the old version hardcoded white-on-dark colours and its own purple, which turned into white text on a white bar in the
 * light theme.
 */
export function AudioChanFilterStrip({ filters, onChange }: AudioChanFilterStripProps) {
  const t = useTranslations("audiochanFilterStrip")
  return (
    <div className={cn(SOURCE_FILTER_ROW, "flex-wrap")}>
      {CONTENT_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange({ ...filters, contentMode: opt.value })}
          className={sourceFilterPill(filters.contentMode === opt.value)}
        >
          {opt.label}
        </button>
      ))}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className={`${sourceFilterPill(filters.audience !== ANY)} inline-flex items-center gap-1`}>
            {filters.audience === ANY ? "Audience" : filters.audience}
            <ChevronDown className="h-3 w-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[9rem]">
          <DropdownMenuRadioGroup
            value={filters.audience}
            onValueChange={(value) => onChange({ ...filters, audience: value as AudioChanFilters["audience"] })}
          >
            <DropdownMenuRadioItem value={ANY}>{t("allAudiences")}</DropdownMenuRadioItem>
            {AUDIENCE_OPTIONS.map((value) => (
              <DropdownMenuRadioItem key={value} value={value}>{value}</DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      {SORT_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange({ ...filters, sort: opt.value })}
          className={sourceFilterPill(filters.sort === opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}
